import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useState } from 'react';
import Observer from '@researchgate/react-intersection-observer';
import classnames from 'classnames';
import parseClassnames from "../helpers/parseClassnames";
import { screens } from "../styles/breakpoints";
import { DEFAULT_PLACEHOLDER } from "./constants";
import getImgxUrl from "./getImgxUrl";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Requeriments:
 * - It must have a better api
 * - It must use picture, source and image html5 tags
 * - it must lazy load the images
 * - it must handle the dpr x1 and x2
 * - it must add a "progressive animation" (TODO)
 * - it must use auto=compress (this will serve the image in webp if the browser supports it)
 *
 *
 * ==== Animation ====
 * For the animation we need to use the onload event.
 * 1. Show the placeholder
 * 2. Detect the image is in the viewport (using intersection observer) and then start to load the real image.
 * 3. Once the real image is loaded (using the onload event) we change its opacity and transform css properties
 * 4. Hide the placeholder and show the real one.
 *
 * ==== Use will-transform ====
 * We are going to set the will-transform css property dynamically.
 * This property is used to help the browser to optimize the relayout/repaint of the elements.
 * 1. One the image has been "pre-loaded",  set the will-transform property to the picture element
 * 2. After 300ms (when the animation ends), remove the will-transform property
 */
/**
 * This component must be used only in next.js application.
 */
var LazyImageResponsive = function LazyImageResponsive(_ref) {
  var placeholder = _ref.placeholder,
    sources = _ref.sources,
    imgxParams = _ref.imgxParams,
    observerProps = _ref.observerProps,
    alt = _ref.alt,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$imgClassName = _ref.imgClassName,
    imgClassName = _ref$imgClassName === void 0 ? '' : _ref$imgClassName,
    _ref$style = _ref.style,
    style = _ref$style === void 0 ? {} : _ref$style,
    _ref$pictureStyle = _ref.pictureStyle,
    pictureStyle = _ref$pictureStyle === void 0 ? {} : _ref$pictureStyle,
    backgroundImage = _ref.backgroundImage,
    dark = _ref.dark,
    isDark = _ref.isDark,
    isTransparent = _ref.isTransparent;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isIntersecting = _useState2[0],
    setIsIntersecting = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isResourceLoaded = _useState4[0],
    setIsResourceLoaded = _useState4[1];
  var commonImgParams = _objectSpread({
    // auto: 'compress',
    fm: 'webp'
  }, imgxParams);
  var defaultPlaceholderTheme = isDark || dark ? 'dark' : 'light';
  if (isTransparent) {
    defaultPlaceholderTheme = 'transparent';
  }
  var placeholderImage = placeholder || DEFAULT_PLACEHOLDER[defaultPlaceholderTheme];
  var defaultWebpImage = getImgxUrl(sources["default"], commonImgParams);
  var defaultImage = getImgxUrl(sources["default"], imgxParams);
  var handleIntersection = function handleIntersection(event, unobserve) {
    if (event.isIntersecting) {
      setIsIntersecting(true);
      unobserve();
    }
  };
  var getSourceTagBySize = function getSourceTagBySize(size) {
    var sourceImage = sources[size];
    if (!sourceImage) {
      return null;
    }
    var image = getImgxUrl(sourceImage, commonImgParams);
    return /*#__PURE__*/_jsx("source", {
      media: "(min-width: ".concat(screens[size], ")"),
      srcSet: "".concat(image, "&dpr=1 1x, ").concat(image, "&dpr=2 2x"),
      type: "image/webp"
    });
  };
  var options = _objectSpread({
    onChange: handleIntersection,
    rootMargin: '0px 0px 300px 0px',
    disabled: isResourceLoaded
  }, observerProps);
  if (process.env.NODE_ENV === 'development') {
    if (!sources.sm || !sources.md || !sources.lg) {
      console.error('LazyImageResponsive: Hey, all keys of the sources props are required! Looks like you are missing one of them.');
    }
  }
  return /*#__PURE__*/_jsx(Observer, _objectSpread(_objectSpread({}, options), {}, {
    children: /*#__PURE__*/_jsxs("div", {
      className: parseClassnames(className, classnames("block max-w-full h-auto", {
        relative: !className.includes('absolute')
      })),
      children: [/*#__PURE__*/_jsx("div", {
        className: "absolute inset-0 z-10",
        style: {
          opacity: isResourceLoaded ? 0 : 1,
          backgroundImage: backgroundImage || "url(".concat(placeholderImage, ")"),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'opacity 0.3s ease-in-out',
          transitionDelay: '100ms',
          willChange: isResourceLoaded ? 'unset' : 'opacity'
        }
      }), isIntersecting && /*#__PURE__*/_jsxs("picture", {
        className: "absolute inset-0",
        style: pictureStyle,
        children: [getSourceTagBySize('lg'), getSourceTagBySize('md'), getSourceTagBySize('sm'), /*#__PURE__*/_jsx("source", {
          srcSet: "".concat(defaultWebpImage, "&dpr=1 1x, ").concat(defaultWebpImage, "&dpr=2 2x"),
          media: "(max-width: 767px)",
          type: "image/webp"
        }), /*#__PURE__*/_jsx("img", {
          style: _objectSpread({
            opacity: isResourceLoaded ? 1 : 0,
            transition: 'opacity 300ms ease-in-out'
          }, style),
          className: parseClassnames(imgClassName, 'w-full h-full block relative object-contain'),
          srcSet: "".concat(defaultImage, "&dpr=1 1x, ").concat(defaultImage, "&dpr=2 2x"),
          src: defaultImage,
          alt: alt
          // this little one is the trick
          ,
          onLoad: function onLoad() {
            return setIsResourceLoaded(true);
          },
          decoding: "async"
        })]
      })]
    })
  }));
};
export default LazyImageResponsive;